import React from "react";
import { Button } from "@mui/material";
import { Form, Link } from "react-router-dom";
import { FullPagePaperLayout } from "../Layouts/FullPagePaperLayout";
import { PageBreadcrumbs, PageTitle, PageActions, PageSubtitle } from "../Layouts/components/PageTitleContainer";
import { LocationTypeMovementCondition } from "types/locationManagement/locationTypeMovementCondition";
import { RevivnDatabaseDataGrid } from "components/DataGrid/RevivnDatabaseDataGrid";
import { linkColDef } from "components/DataGrid/columnDefs/linkColDef";
import { actionColDef } from "components/DataGrid/columnDefs/actionColDef";
import { GridValueGetterParams } from "@mui/x-data-grid-pro";

const LocationTypeMovementConditionList: React.FC = () => {
  return (
    <FullPagePaperLayout>
      <PageBreadcrumbs
        links={[
          { label: "Admin" },
          { label: "Location Type Movement Conditions", path: "/location-type-movement-conditions" },
        ]}
      />
      <PageTitle>Movement Conditions</PageTitle>
      <PageSubtitle>
        Define conditions that must be met for inventory to move between location types. These conditions will be
        checked before allowing the movement to ensure inventory meets the required criteria.
      </PageSubtitle>
      <PageActions>
        <Button variant="contained" color="primary" component={Link} to="/location-type-movement-conditions/new">
          Create Movement Condition
        </Button>
      </PageActions>
      <RevivnDatabaseDataGrid
        url="/location_management/location_type_movement_conditions"
        syncStateWithSearchParams
        columns={[
          { field: "id", headerName: "ID", width: 90, filterable: false },
          {
            field: "fromLocationType.name",
            headerName: "From Location Type",
            width: 200,
            filterable: false,
            valueGetter: (params: GridValueGetterParams) => params.row.fromLocationType?.name || "Any Location Type",
            ...linkColDef<LocationTypeMovementCondition>(
              ({ row }) => `/location-types/${row.fromLocationType?.id}/edit`,
            ),
          },
          {
            field: "toLocationType.name",
            headerName: "To Location Type",
            width: 200,
            filterable: false,
            valueGetter: (params: GridValueGetterParams) => params.row.toLocationType?.name || "Any Location Type",
            ...linkColDef<LocationTypeMovementCondition>(({ row }) => `/location-types/${row.toLocationType?.id}/edit`),
          },
          {
            field: "inventoryAttribute",
            headerName: "Inventory Attribute",
            width: 150,
            filterable: false,
          },
          {
            field: "conditionType",
            headerName: "Condition Type",
            width: 150,
            filterable: false,
          },
          {
            field: "conditionValue",
            headerName: "Condition Value",
            width: 200,
            filterable: false,
          },
          {
            field: "description",
            headerName: "Description",
            width: 250,
            filterable: false,
          },
          {
            field: "edit",
            headerName: "Edit",
            width: 120,
            sortable: false,
            filterable: false,
            valueGetter: (params: GridValueGetterParams) => "Edit",
            ...linkColDef<LocationTypeMovementCondition>(
              ({ row }) => `/location-type-movement-conditions/${row.id}/edit`,
            ),
          },
          {
            field: "delete",
            headerName: "Delete",
            width: 120,
            sortable: false,
            filterable: false,
            valueGetter: (params: GridValueGetterParams) => "Delete",
            ...actionColDef<LocationTypeMovementCondition>(
              ({ row }) => ({ method: "delete", action: `/location-type-movement-conditions/${row.id}/delete` }),
              <Button
                type="submit"
                variant="outlined"
                color="error"
                size="small"
                onClick={(e) => {
                  if (!confirm("Are you sure you want to delete this movement condition?")) {
                    e.preventDefault();
                  }
                }}
              >
                Delete
              </Button>,
            ),
          },
        ]}
      />
    </FullPagePaperLayout>
  );
};

export default LocationTypeMovementConditionList;
